import { extendObservable, computed } from 'mobx'
import moment from 'moment'

import { teamApi } from '@atheneum/api'
import { stringifyYearMonth } from '@atheneum/api/dist/lib'

class Team {
	constructor(staticData, growlStore) {
		this.staticData = staticData
		this.growlStore = growlStore
		this.POSITIONS = staticData.CONST.EMPLOYEE_POSITIONS

		extendObservable(this, {
			loading: false,
			employees: [],
			teams: [],
			monthYear: stringifyYearMonth(new Date()),
			nextMonthStart: moment()
				.add(1, 'month')
				.startOf('month'),
			atheneumOfficeList: [],
			isEditable: computed(() => {
				if (!this.monthYear) return false
				const now = moment()
				const queryDate = moment(this.monthYear, 'YYYY-MM')
				const prevMonthStart = moment()
					.startOf('month')
					.subtract(1, 'months')
				if (now.month() === queryDate.month()) return true
				if (
					prevMonthStart.isSame(queryDate) &&
					now.date() <= this.staticData.teamEditCutoffDate
				) {
					return true
				}
				return false
			}),
			position: null
		})
	}

	scrollToTeamId(teamId) {
		const elem = document.getElementById(`scroll-anchor-${teamId}`)

		if (!elem) return

		this.position = elem.offsetTop - 25
		this.scrollToPosition()
	}

	scrollToTeamName(teamName) {
		const elem = document.getElementById(teamName)

		if (!elem) return

		this.position = elem.offsetTop - 25
		this.scrollToPosition()
	}

	scrollToPosition() {
		if (this.position) {
			document.getElementsByClassName(
				'scroll-inner'
			)[1].scrollTop = this.position
		}
	}

	loadData = async () => {
		try {
			this.loading = true

			const date = moment(this.monthYear, 'YYYY-MM')
			const employees = await teamApi.getAllEmployees(date.year(), date.month())
			const teams = await teamApi.getActiveTeams()

			this.employees.replace(
				employees.map(e => {
					e.targetMargin = e.targetMargin.toFixed(2)
					return e
				})
			)
			this.teams.replace(teams)
			this.mapAtheneumOfficeList()

			this.loading = false
		} catch (err) {
			throw err
		}
	}

	updateEmployeeTargetMargin = teamApi.updateEmployeeTargetMargin

	changeMonthYear = date => {
		this.monthYear = stringifyYearMonth(date)
		this.nextMonthStart = date.add(1, 'month').startOf('month')
		this.loadData()
	}

	fetchTeamMembers = async id => {
		try {
			this.loading = true

			const employees = await teamApi.getTeamMembers(id)

			this.employees.replace(employees)

			this.loading = false
		} catch (err) {
			throw err
		}
	}

	createTeam = async data => {
		const date = moment(this.monthYear, 'YYYY-MM')
		data.year = date.year()
		data.month = date.month()
		const team = await teamApi.create(data)
		await this.loadData()
		return team
	}

	updateTeam = async (data, teamId) => {
		const date = moment(this.monthYear, 'YYYY-MM')
		data.year = date.year()
		data.month = date.month()
		const team = await teamApi.update(teamId, data)
		await this.loadData()
		return team
	}

	init = async () => {
		await this.loadData()
	}

	initTeamMembers(id) {
		return this.fetchTeamMembers(id)
	}

	assignEmployee = async ({ teamId, supervisorId, employeeId, isSenior = false }) => {
		const date = moment(this.monthYear, 'YYYY-MM')
		await teamApi.assignEmployee({
			teamId,
			supervisorId,
			employeeId,
			isSenior,
			year: date.year(),
			month: date.month()
		})
		const employee = this.employees.find(e => e.id === employeeId)
		employee.supervisorId = supervisorId
		employee.teamId = teamId
		await this.loadData()
	}

	removeEmployee = async ({ employeeId }) => {
		const date = moment(this.monthYear, 'YYYY-MM')
		teamApi.removeEmployee({
			employeeId,
			year: date.year(),
			month: date.month()
		})
			.then(() => {
				this.loadData()
			})
			.catch(error => {
				this.growlStore.showError(error)
			})
	}

	removeTeam = async ({ teamId }) => {
		const date = moment(this.monthYear, 'YYYY-MM')

		await teamApi.removeTeam(teamId, date.year(), date.month())
		this.loadData()
	}

	getAccountManagerOrPrincipal = () =>
		this.employees.filter(
			e =>
				e.status &&
				!e.teamId &&
				[this.POSITIONS.ACCOUNT_MANAGER, this.POSITIONS.PRINCIPAL].includes(
					e.positionId
				)
		)

	getTeamLeaders = () =>
		this.employees.filter(
			e =>
				e.status &&
				e.positionId === this.POSITIONS.TEAM_LEADER &&
				!e.supervisorId
		)

	mapAtheneumOfficeList = () => {
		const employees = this.employees.filter(
			entry => entry.atheneumOfficeName !== null
		)

		let offices = {
			Unassigned: {
				id: null,
				name: 'Unassigned',
				expanded: true,
				employees: []
			}
		}
		employees.forEach(employee => {
			if (!offices[employee.atheneumOfficeName]) {
				offices[employee.atheneumOfficeName] = {
					id: employee.atheneumOfficeId,
					name: employee.atheneumOfficeName,
					expanded: true,
					employees: [employee]
				}
			} else {
				offices[employee.atheneumOfficeName].employees.push(employee)
			}
		})

		this.atheneumOfficeList = offices
	}

	getAssociates = () =>
		this.employees.filter(
			e =>
				e.status &&
				!e.supervisorId &&
				[this.POSITIONS.ASSOCIATE, this.POSITIONS.ASSOCIATE_INTERN].includes(
					e.positionId
				)
		)

	getUnassigned = () => this.employees.filter(e => !e.supervisorId && !e.teamId)

	getAssociatesBySupervisor = supervisorId =>
		this.employees.filter(
			e =>
				e.supervisorId === supervisorId &&
				e.positionId !== this.POSITIONS.TEAM_LEADER
		)

	getTeamLeadersBySupervisor = supervisorId =>
		this.employees.filter(
			e =>
				e.supervisorId === supervisorId &&
				e.positionId === this.POSITIONS.TEAM_LEADER
		)

	getPrincipals = supervisorId =>
		this.employees.filter(
			e => e.status && e.positionId === this.POSITIONS.PRINCIPAL
		)

	export = async () => {
		const year = this.monthYear.split('-').shift()
		// using 0-offset date in backend
		const month = this.monthYear.split('-').pop() - 1

		const { jobId } = await teamApi.exportEmployees(year, month)

		return jobId
	}
}

export default Team
